import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { scopeKey, topicLabel, UNCATEGORIZED, unfollowNote, type Filter, type Scope } from "@vladrss/core";
import { Icon } from "./Icon";
import { FeedIcon } from "./List";
import { api, useOwnerMutation, useOwnerQuery } from "../lib/api";
import { useDragInto, useDragOrder } from "../lib/drag";
import { useKeptScroll } from "../lib/kept";
import { useArticleActions, useCategories, useCategoryActions, useCounts, useFeeds, useTopicActions, useTopics } from "../lib/library";
import { effectiveMode, setPrefs, toggleNav, usePrefs } from "../lib/prefs";
import { openSettings, setView, toast, update, useStore } from "../lib/store";

const EXPANDED_KEY = "vladrss.expanded";
const loadExpanded = (): Record<string, boolean> => { try { return JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? "{}"); } catch { return {}; } };

export function Sidebar({ onNavigate, onCollapse }: { onNavigate?: () => void; onCollapse?: () => void }) {
  const { forScope } = useCounts();
  const scope = useStore((st) => st.scope);
  const filter = useStore((st) => st.filter);
  usePrefs(); // re-render when the theme changes
  // Saved is a place of its own: All sources, showing saved articles
  const cur = filter === "saved" && scope.type === "all" ? "saved" : scopeKey(scope);
  const all = forScope({ type: "all" });
  const go = (s: Scope, f?: Filter) => { setView(s, f); onNavigate?.(); };
  // a place with nothing unread shows no number; a topic shows a quiet ✓, which says on hover how many it has
  const count = (n: number, tick = false) => n ? <span className="cnt">{n}</span> : tick ? <span className="cnt zero">✓</span> : null;
  const scroll = useKeptScroll<HTMLDivElement>("sidebar");

  return (
    <nav className="wnav" aria-label="Sources">
      <div className="nav-top">
        <span className="brand"><span className="wm"><Icon name="rss" size={18} /></span><b>Vlad</b><small>RSS</small></span>
        <button className="ib" onClick={onCollapse ?? toggleNav} aria-label="Collapse sources" data-tip="Collapse" data-key="["><Icon name="sidebar" /></button>
      </div>
      <button className="jump" onClick={() => update({ modal: "palette" })} aria-label="Search or jump to"><Icon name="search" size={16} /><span className="l">Search or jump to…</span><kbd>{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"} K</kbd></button>
      <div className="nav-scroll" ref={scroll.ref} onScroll={scroll.onScroll}>
        <button className={`ni${cur === "all" ? " on" : ""}`} onClick={() => go({ type: "all" })} data-tip-rail="All sources"><Icon name="inbox" /><span className="lbl">All sources</span>{count(all.unread)}</button>
        <button className={`ni${cur === "saved" ? " on" : ""}`} onClick={() => { setView({ type: "all" }, "saved"); onNavigate?.(); }} data-tip-rail="Saved"><Icon name="bookmark" /><span className="lbl">Saved</span><span className="cnt zero">{all.saved || ""}</span></button>
        <button className={`ni${cur === "recent" ? " on" : ""}`} onClick={() => go({ type: "recent" })} data-tip-rail="Recently read"><Icon name="clock" /><span className="lbl">Recently read</span><span /></button>
        <Topics cur={cur} go={go} counts={(name) => forScope({ type: "topic", name })} count={count} />
        <div className="nav-h"><span className="caps">Sources</span><button className="ib sm" onClick={() => openSettings("sources", true)} aria-label="Add a source" data-tip="Add a source"><Icon name="plus" size={16} /></button></div>
        <Categories cur={cur} go={go} count={count} />
      </div>
      <div className="nav-foot">
        <button className="ni" onClick={() => openSettings("sources")}><Icon name="sliders" /><span className="lbl">Settings</span><kbd>,</kbd></button>
        <button className="ni quiet keys-only" onClick={() => update({ modal: "help" })}><Icon name="kbd" /><span className="lbl">Keyboard shortcuts</span><kbd>?</kbd></button>
        <div className="foot-row"><SyncLine /><button className="ib sm" onClick={() => setPrefs({ theme: effectiveMode() === "dark" ? "light" : "dark" })} aria-label="Switch theme" data-tip={effectiveMode() === "dark" ? "Light theme" : "Dark theme"}><Icon name={effectiveMode() === "dark" ? "sun" : "moon"} size={16} /></button></div>
      </div>
    </nav>
  );
}

function SyncLine() {
  const status = useOwnerQuery(api.feeds.status, {});
  const at = status?.lastCheckAt;
  const ago = at ? Math.round((Date.now() - at) / 60_000) : null;
  return <span className="sync"><span className="sd" />{ago === null ? "Not checked yet" : ago < 1 ? "Checked just now" : `Checked ${ago} min ago`}</span>;
}

/**
 * The sources, by category, in the owner's order. Drag a source onto a category to move it
 * there: while it is over one, a preview shows where it will land (in a folded category too),
 * and on letting go it glides into that place. Drag a category to put it elsewhere; right-click
 * (or long-press) a category to rename it, or to merge it into another by giving it that name.
 * Uncategorized stays last.
 */
function Categories({ cur, go, count }: { cur: string; go: (s: Scope) => void; count: (n: number) => ReactNode }) {
  const feeds = useFeeds();
  const cats = useCategories();
  const { forScope, byFeed } = useCounts();
  const { reorder, moveSource, rename } = useCategoryActions();
  const [expanded, setExpanded] = useState(loadExpanded);
  const [editing, setEditing] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ name: string; x: number; y: number } | null>(null);
  // a source being carried, and the category it is over: where its preview shows
  const [aim, setAim] = useState<{ id: string; into: string } | null>(null);
  // a source's menu (right-click or long-press), and a name typed in its place: a new name, or a new category
  const [sourceMenu, setSourceMenu] = useState<{ id: string; x: number; y: number; confirm?: boolean } | null>(null);
  const [sourceEdit, setSourceEdit] = useState<{ id: string; mode: "rename" | "category" } | null>(null);
  const renameSource = useOwnerMutation(api.feeds.rename);
  const unfollowSource = useOwnerMutation(api.feeds.unfollow);
  const live = (feeds ?? []).filter((f) => !f.deleted);
  const setOpen = (c: string, open: boolean) => {
    const next = { ...expanded, [c]: open }; // folded only when stored as false
    setExpanded(next);
    try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const order = useDragOrder(reorder);
  const carry = useDragInto({
    over: (id, into) => setAim(into ? { id, into } : null),
    // a folded category opens, so the source is seen to land
    drop: (id, into) => { setAim(null); if (expanded[into] === false) setOpen(into, true); moveSource(id, into); },
  });
  const carried = aim && live.find((f) => f.id === aim.id);

  return (
    <div className="cats" ref={order.box}>
      {cats.map((c, i) => {
        const open = expanded[c] !== false;
        const fixed = c === UNCATEGORIZED; // always last: not moved
        const sources = live.filter((f) => f.category === c);
        const preview = carried && aim?.into === c ? carried : null;
        // the preview takes its place among them, alphabetically; a folded category shows it alone
        const rows = [...(open ? sources : []), ...(preview ? [preview] : [])].sort((a, b) => a.title.localeCompare(b.title));
        return (
          <div className={`cat${open ? " open" : ""}`} key={c} data-drag={fixed ? undefined : c} data-drop={c}>
            <div className="cat-row">
              <button className="chev" onClick={() => { if (!order.swallow()) setOpen(c, !open); }} aria-label={`${open ? "Hide" : "Show"} sources in ${c}`} aria-expanded={open}><Icon name="chevR" size={14} /></button>
              {editing === c
                ? <InlineField initial={c} label={`Rename ${c}`} placeholder="Category name" onDone={(v) => { setEditing(null); if (v !== null) void rename(c, v); }} />
                : (
                  <button className={`ni${cur === `cat:${c}` ? " on" : ""}`}
                    onClick={() => { if (order.swallow()) return; if (cur === `cat:${c}`) setOpen(c, !open); else { if (!open) setOpen(c, true); go({ type: "category", name: c }); } }}
                    onPointerDown={fixed ? undefined : order.start}
                    onKeyDown={fixed ? undefined : (e) => order.key(e, i, c)}
                    onContextMenu={(e) => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ name: c, x: e.clientX || r.left + 24, y: e.clientY || r.bottom }); }}
                    aria-expanded={open} aria-haspopup="menu" data-tip-rail={c}>
                    <span className="lbl">{c}</span>{count(forScope({ type: "category", name: c }).unread)}
                  </button>
                )}
            </div>
            {rows.length > 0 && <div className="feeds">{rows.map((f) => f === preview ? (
              <div key="preview" className="ni ghost" aria-hidden="true"><FeedIcon feed={f} /><span className="lbl">{f.title}</span><span /></div>
            ) : sourceEdit?.id === f.id ? (
              <InlineField key={f.id} initial={sourceEdit.mode === "rename" ? f.title : ""} label={sourceEdit.mode === "rename" ? `Rename ${f.title}` : `New category for ${f.title}`}
                placeholder={sourceEdit.mode === "rename" ? "Source name" : "New category name"}
                onDone={(v) => {
                  setSourceEdit(null);
                  const name = v?.replace(/\s+/g, " ").trim();
                  if (!name) return;
                  if (sourceEdit.mode === "category") { setOpen(name, true); moveSource(f.id, name); }
                  else if (name !== f.title) renameSource({ id: f.id as never, title: name }).then(() => toast(`Renamed to ${name}`), () => toast("Couldn't reach the server · try again"));
                }} />
            ) : (
              <button key={f.id} data-carry={f.id} className={`ni${cur === `feed:${f.id}` ? " on" : ""}`}
                onClick={() => { if (!carry.swallow()) go({ type: "feed", feedId: f.id }); }}
                onPointerDown={(e) => carry.start(e, f.id, c)}
                onContextMenu={(e) => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setSourceMenu({ id: f.id, x: e.clientX || r.left + 24, y: e.clientY || r.bottom }); }}
                aria-haspopup="menu"
                title={f.failingSince ? `Failing: ${f.lastError ?? ""}` : undefined}>
                <FeedIcon feed={f} /><span className="lbl">{f.title}</span>{count(byFeed.get(f.id)?.unread ?? 0)}
              </button>
            ))}</div>}
          </div>
        );
      })}
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} label={menu.name} onClose={() => setMenu(null)} items={[
          { icon: "pencil", label: "Rename", run: () => setEditing(menu.name) },
        ]} />
      )}
      {sourceMenu && (() => {
        const f = live.find((x) => x.id === sourceMenu.id);
        if (!f) return null;
        const close = () => setSourceMenu(null);
        // unfollowing asks once more, in the same menu
        const counts = byFeed.get(f.id);
        const impact = counts?.unreadLeaving == null ? null : unfollowNote({ ...counts, unreadLeaving: counts.unreadLeaving });
        const items: MenuItem[] = sourceMenu.confirm ? [
          { heading: `Unfollow ${f.title}? ${impact ?? "Article counts are updating."}`, question: true },
          ...(impact === null ? [] : [{ icon: "minusC", label: "Unfollow", danger: true, run: () => { unfollowSource({ id: f.id as never }).then(() => toast(`Unfollowed ${f.title}`), () => toast("Couldn't reach the server · try again")); } }]),
          { label: "Cancel", run: () => {} },
        ] : [
          { icon: "pencil", label: "Rename", run: () => setSourceEdit({ id: f.id, mode: "rename" }) },
          { rule: true },
          { heading: "Move to" },
          ...cats.filter((c) => c !== f.category).map((c) => ({ icon: "folder", label: c, run: () => { if (expanded[c] === false) setOpen(c, true); moveSource(f.id, c); } })),
          { icon: "plus", label: "New category…", run: () => setSourceEdit({ id: f.id, mode: "category" }) },
          { rule: true },
          { icon: "minusC", label: "Unfollow…", stay: true, run: () => setSourceMenu({ ...sourceMenu, confirm: true }) },
        ];
        return <ContextMenu x={sourceMenu.x} y={sourceMenu.y} label={f.title} onClose={close} items={items} />;
      })()}
    </div>
  );
}

/**
 * Followed topics, listed like sources: each gathers the articles that name it, from every source.
 * The last row follows a new one in place. Drag a topic to move it; right-click (or long-press)
 * it to rename it or stop following it.
 */
function Topics({ cur, go, counts, count }: { cur: string; go: (s: Scope, f?: Filter) => void; counts: (name: string) => { unread: number; total: number }; count: (n: number, tick?: boolean) => ReactNode }) {
  const topics = useTopics();
  const { follow, rename, unfollow, move } = useTopicActions();
  const { markAll } = useArticleActions();
  const [editing, setEditing] = useState<string | null>(null); // a topic being renamed, or "" for a new one
  const [menu, setMenu] = useState<{ name: string; x: number; y: number } | null>(null);
  const drag = useDragOrder(move);
  if (!topics) return null;

  const openMenu = (e: MouseEvent, name: string) => {
    e.preventDefault();
    // from the keyboard's menu key there is no pointer: open beside the row
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenu({ name, x: e.clientX || r.left + 24, y: e.clientY || r.bottom });
  };

  return (
    <div className="topics" ref={drag.box}>
      {topics.map((t, i) => editing === t
        ? <InlineField key={t} icon="hash" initial={t} label={`Rename ${topicLabel(t)}`} placeholder={TOPIC_PLACEHOLDER} onDone={(v) => { setEditing(null); if (v !== null) rename(t, v); }} />
        : (
          <button key={t} data-drag={t} className={`ni${cur === scopeKey({ type: "topic", name: t }) ? " on" : ""}`}
            // a topic with nothing unread opens on everything that names it, not on an empty page
            onClick={() => { if (!drag.swallow()) go({ type: "topic", name: t }, counts(t).unread ? undefined : "all"); }}
            onPointerDown={drag.start}
            onKeyDown={(e) => drag.key(e, i, t)}
            onContextMenu={(e) => openMenu(e, t)} aria-haspopup="menu"
            title={t.includes(",") ? t : undefined} aria-description="Drag, or Alt and an arrow key, to move"
            data-tip={counts(t).unread ? undefined : `${counts(t).total} ${counts(t).total === 1 ? "article" : "articles"}, all read`}>
            <Icon name="hash" /><span className="lbl">{topicLabel(t)}</span>{count(counts(t).unread, true)}
          </button>
        ))}
      {editing === ""
        ? <InlineField icon="hash" initial="" label="Follow a topic" placeholder={TOPIC_PLACEHOLDER} hint="Add other names with commas" onDone={(v) => { setEditing(null); const name = v === null ? null : follow(v); if (name) go({ type: "topic", name }); }} />
        : <button className="ni quiet" onClick={() => setEditing("")}><Icon name="plus" /><span className="lbl">Follow a topic…</span><span /></button>}
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} label={topicLabel(menu.name)} onClose={() => setMenu(null)} items={[
          ...(counts(menu.name).unread ? [{ icon: "checks", label: "Mark all read", run: () => void markAll(topicLabel(menu.name), { type: "topic", name: menu.name }) }] : []),
          { icon: "pencil", label: "Rename", run: () => setEditing(menu.name) },
          { icon: "minusC", label: "Stop following", run: () => unfollow(menu.name) },
        ]} />
      )}
    </div>
  );
}

const TOPIC_PLACEHOLDER = "A word, name or phrase";

/** A name typed in place of its row. Enter or leaving the field keeps it; Escape (or nothing typed) does not. */
function InlineField({ icon, initial, label, placeholder, hint, onDone }: { icon?: string; initial: string; label: string; placeholder: string; hint?: string; onDone: (value: string | null) => void }) {
  const done = useRef(false);
  const finish = (v: string | null) => { if (done.current) return; done.current = true; onDone(v && v.trim() ? v : null); };
  return (
    <form className={`inline-field${icon ? "" : " bare"}`} onSubmit={(e) => { e.preventDefault(); finish((e.currentTarget.elements.namedItem("t") as HTMLInputElement).value); }}>
      <div className="ni on">{icon && <Icon name={icon} />}
        <input name="t" className="tf" defaultValue={initial} placeholder={placeholder} aria-label={label} autoFocus maxLength={160} spellCheck={false} autoComplete="off"
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); finish(null); } }}
          onBlur={(e) => finish(e.currentTarget.value)} />
      </div>
      {hint && <small className="tf-hint">{hint}</small>}
    </form>
  );
}

/** One line of a menu: a choice, a heading over the choices after it (or a question they answer), or a rule between groups. */
type MenuItem =
  | { icon?: string; label: string; run: () => void; danger?: boolean; /** leads to a next step in the same menu */ stay?: boolean }
  | { heading: string; question?: boolean }
  | { rule: true };

/** A small menu at the pointer: closes on a choice, Escape, a click elsewhere or scrolling. */
function ContextMenu({ x, y, label, items, onClose }: { x: number; y: number; label: string; items: MenuItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState({ left: x, top: y });
  // kept inside the window
  useLayoutEffect(() => {
    const r = ref.current!.getBoundingClientRect();
    setAt({ left: Math.min(x, innerWidth - r.width - 8), top: Math.min(y, innerHeight - r.height - 8) });
    ref.current!.querySelector<HTMLButtonElement>("button")?.focus();
  }, [x, y, items.length]);
  useEffect(() => {
    const away = (e: Event) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation(); // the list's arrow keys wait
        const bs = [...ref.current!.querySelectorAll("button")], i = bs.indexOf(document.activeElement as HTMLButtonElement);
        bs[(i + (e.key === "ArrowDown" ? 1 : bs.length - 1)) % bs.length]?.focus();
      }
    };
    addEventListener("pointerdown", away, true); addEventListener("keydown", key, true); addEventListener("scroll", onClose, true); addEventListener("blur", onClose);
    return () => { removeEventListener("pointerdown", away, true); removeEventListener("keydown", key, true); removeEventListener("scroll", onClose, true); removeEventListener("blur", onClose); };
  }, [onClose]);
  return (
    <div ref={ref} className="menu ctx" role="menu" aria-label={label} style={at}>
      {items.map((it, i) => "rule" in it ? <div key={`r${i}`} className="menu-sep" role="separator" />
        : "heading" in it ? <div key={`h${i}`} className={it.question ? "mq" : "caps mh"}>{it.heading}</div>
        : <button key={it.label} className={`mi${it.danger ? " danger" : ""}`} role="menuitem" onClick={() => { if (!it.stay) onClose(); it.run(); }}>{it.icon && <Icon name={it.icon} size={16} />}{it.label}</button>)}
    </div>
  );
}
